import { onMounted, ref, onBeforeUnmount, reactive } from 'vue';
import { useStore } from 'vuex'

//封装语音播报函数

// 语音播报配置
interface SpeechOptions {
    text: string;               // 要播报的文本
    lang?: string;              // 语言  例如：'zh-CN'
    rate?: number;              // 语速  例如：0.5
    pitch?: number;             // 语调  例如：0.5
    volume?: number;            // 音量  例如：0.5
    voice?: SpeechSynthesisVoice; // 语音  例如：'zh-CN'
    onStart?: () => void;       // 开始播报回调
    onEnd?: () => void;         // 结束播报回调
    onError?: (event: SpeechSynthesisErrorEvent) => void;       // 错误回调
}

// 语音播报状态
interface SpeechState {
    isSupported: boolean         // 是否支持语音播报
    isSpeaking: boolean         // 是否正在播报
    isPaused: boolean          // 是否暂停
    voices: SpeechSynthesisVoice[] // 语音列表
}

export default function useSpeechSynthesis() {
    const store = useStore()
    // 语音合成对象
    const synth = window.speechSynthesis;
    // 当前语音对象
    const utteranceState = reactive({
        currentUtterance: null as SpeechSynthesisUtterance | null,
    });
    // 语音播报状态
    const state = reactive<SpeechState>({
        isSupported: 'speechSynthesis' in window,
        isSpeaking: false,
        isPaused: false,
        voices: [],
    });

    //清理当前的utterance
    const clearCurrentUtterance = () => {
        if (utteranceState.currentUtterance) {
            // 移除所有事件监听器以避免内存泄漏
            utteranceState.currentUtterance.onstart = null;
            utteranceState.currentUtterance.onend = null;
            utteranceState.currentUtterance.onerror = null;
            utteranceState.currentUtterance.onboundary  = null;
            utteranceState.currentUtterance.onmark = null;
            utteranceState.currentUtterance = null;
        }
        state.isSpeaking = false;
        state.isPaused = false;
    }


    // 获取可用的语音列表
    const loadVoices = () => {
        if (!state.isSupported) return;
        return new Promise<SpeechSynthesisVoice[]>((resolve) => {
            const voices = synth.getVoices();
            if(voices.length > 0){
                state.voices = voices;
                resolve(voices);
            }else{
                synth.onvoiceschanged = () => {
                    const voices = synth.getVoices();
                    state.voices = voices;
                    resolve(voices);
                }
            }
        });
    }

    // 语音播报
    const speak = (options: SpeechOptions | string) => {
        if(!state.isSupported){
            console.warn('当前浏览器不支持语音播报');
            return;
        }
        if(!options){
            return;
        }

        // 如果传入的是字符串，转换为默认选项
        const speechOptions: SpeechOptions = typeof options === 'string' ? { text: options } : options;
        
        //停止当前的播报
        stop()

        //创建新的语音实例
        const utterance = new SpeechSynthesisUtterance(speechOptions.text);
        utterance.lang = speechOptions.lang || 'zh-CN';
        utterance.rate = speechOptions.rate || 1.7;
        utterance.pitch = speechOptions.pitch || 1;
        utterance.volume = store.state.persistence.sounds.humanSound;

        // if(speechOptions.voice){
        //     utterance.voice = speechOptions.voice;
        // }

        //设置事件监听
        utterance.onstart = () => {
            state.isSpeaking = true;
            state.isPaused = false;
            utteranceState.currentUtterance = utterance;
            speechOptions.onStart?.();
        }

        utterance.onend = () => {
            clearCurrentUtterance();
            speechOptions.onEnd?.();
        }

        utterance.onerror = (event) => {
            // 忽略 'interrupted' 错误，因为这是预期的行为
            if(event.error !== 'interrupted'){
                // console.error('语音播报错误', event);
                speechOptions.onError?.(event)
            }
            clearCurrentUtterance
        }
        
        //开始播报
        try{
            synth.speak(utterance);      
        }catch(error){
            // console.error('SpeechSynthesis speak error:', error)
            clearCurrentUtterance()
        }
    }

    //停止播报
    const stop = () => {
        if(state.isSpeaking && utteranceState.currentUtterance){
            try{
                synth.cancel();
            }catch(error){
                console.error('SpeechSynthesis cancel error:', error)
            }finally{ 
                clearCurrentUtterance();
            }
        }
    }

    //暂停播报
    const pause = () => {
        if(state.isSpeaking && !state.isPaused){
            synth.pause();
            state.isPaused = true;
        }
    }

    // 恢复播报
    const resume = () => {
        if (state.isPaused) {
            synth.resume()
            state.isPaused = false
        }
    }

    // 组件卸载时停止播报
    onBeforeUnmount(() => {
        stop()
    })

    // 初始化时加载语音列表
    // if (state.isSupported) {
    //     loadVoices()
    // }

    return {
        speakState:state,
        synth,
        speak,
        stop,
        pause,
        resume,
        loadVoices
    }  
}
